<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="flex flex-col text-gray-800 store-page grow">
    <CBreadcrumbs v-if="!onMyStorePage" :breadcrumb="breadcrumb" />

    <div v-if="store" class="flex flex-col grow xl:flex-row">
      <div class="lg:flex lg:flex-col lg:grow lg:mb-10">
        <TheHero :background-url-desktop="heroUrlDesktop" :background-url-mobile="heroUrlMobile" />

        <div class="pt-6 mx-4 text-xs lg:flex lg:flex-col lg:pt-10 lg:mx-auto lg:text-sm lg:max-w-screen-sm">
          <CHeading :level="1" class="mb-6 text-primary-900">{{ store.title }}</CHeading>
          <CText v-if="store.ingress" class="mb-6 text-primary-900" variant="lg">
            {{ store.ingress }}
          </CText>
          <div
            v-if="store.bodyTextPrimary"
            class="mb-6"
            v-html="richTextToHtmlString(store.bodyTextPrimary.json, store.bodyTextPrimary.links)"
          />
          <div class="flex justify-around">
            <div
              v-if="store.specialOpeningHoursWeekdays"
              class="mb-6"
              v-html="
                richTextToHtmlString(store.specialOpeningHoursWeekdays.json, store.specialOpeningHoursWeekdays.links)
              "
            />
            <div
              v-if="store.specialOpeningHoursWeekends"
              class="mb-6"
              v-html="
                richTextToHtmlString(store.specialOpeningHoursWeekends.json, store.specialOpeningHoursWeekends.links)
              "
            />
          </div>
          <div
            v-if="store.infobox"
            class="mb-10"
            v-html="richTextToHtmlString(store.infobox.json, store.infobox.links)"
          />
          <CHeading v-if="assortments.length" :level="2" :class="'mb-4 text-primary-900'">
            {{ t('products_we_offer') }}:
          </CHeading>
          <div v-if="assortments" class="flex flex-wrap justify-center gap-4 mb-2 md:justify-start md:mb-10">
            <AssortmentIcon
              v-for="(assortment, index) in assortments"
              :key="index"
              class="w-28"
              :content="assortment"
            />
          </div>
          <CHeading v-if="services.length" :level="2" :class="'mb-4 text-primary-900'">
            {{ t('services_we_offer') }}:
          </CHeading>
          <div class="flex flex-wrap gap-4">
            <ServiceBanner v-for="(banner, index) in services" :key="index" :content="banner" />
          </div>

          <CHeading v-if="store.contactsSectionTitle" :level="2" :class="'mb-4 text-primary-900'">
            {{ store.contactsSectionTitle }}
          </CHeading>
          <div v-if="contacts" class="flex flex-wrap gap-4 py-4">
            <ContactCard v-for="(contact, index) in contacts" :key="index" :content="contact" />
          </div>
        </div>
        <SectionComponent
          v-for="(section, index) in store.sectionsCollection.items.filter((item) => item !== null)"
          :id="section?.sys?.id"
          :key="index"
          :content-type="section?.__typename"
          class="w-full"
          :section="section"
        />
      </div>

      <div
        class="mx-4 mb-4 overflow-y-hidden bg-white rounded shadow-md lg:mx-auto lg:w-full lg:max-w-screen-sm xl:mx-0 xl:mb-0 xl:rounded-none xl:w-96 xl:shrink-0 xl:shadow-none"
      >
        <StoreDetailsCard v-bind="store" :title="storeName" size="large" />
      </div>
    </div>
    <div v-else-if="onMyStorePage && erpStoreDetails" class="flex items-center justify-center pb-6 grow">
      <StoreDetailsCard
        v-bind="erpStoreDetails"
        class="max-w-xl"
        size="large"
        :render-two-columns="true"
        :static-map="true"
        :padded-address="false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useUserStore, useContentStore, useStoresStore } from '@/stores';
import type { StorePage } from '@/types/contentful';

type ErpStoreDetails = {
  name: string;
  title: string;
  address: string;
  phone: string;
  email: string;
};

type Data = {
  store: StorePage | null;
  erpStoreDetails?: ErpStoreDetails;
};

const { params, name, path } = useRoute();
const { id } = params;
const { t } = useI18n();

const { getPreferredStore } = storeToRefs(useUserStore());
const { getStorePage } = useContentStore();
const { getStore } = useStoresStore();
const { public: config } = useRuntimeConfig();

const storeId = computed<string>(() => (id as string) || getPreferredStore.value?.id);

const { data } = await useAsyncData(
  'storePage',
  async (): Promise<Data> => {
    const { data, error: contentfulError } = await getStorePage({ storeId: storeId.value });

    let erpStoreDetails;
    if (!data && onMyStorePage.value) {
      const erpStore = getStore(storeId.value).value;
      erpStoreDetails = {
        name: erpStore?.name,
        title: erpStore?.name,
        address: `${erpStore?.address}, ${erpStore?.postalCode} ${erpStore?.city}`,
        phone: erpStore?.phone,
        email: erpStore?.email,
      };
    }

    if (contentfulError) {
      createError(contentfulError as string);
      return { store: null, erpStoreDetails };
    }

    // This should never happen now. But keeping this for safety.
    if (!data) {
      createError('No store page found');
      return { store: null, erpStoreDetails };
    }

    return { store: data, erpStoreDetails };
  },
  {
    watch: [getPreferredStore],
  },
);

const store = computed(() => data.value?.store);
const erpStoreDetails = computed<ErpStoreDetails | undefined>(() => data.value?.erpStoreDetails);

const onMyStorePage = computed(() => name === 'my-store');

const breadcrumb = computed(() => ({
  name: store.value?.name,
  parents: [],
}));

const heroUrlDesktop = computed(() => store.value?.heroDesktop?.url);
const heroUrlMobile = computed(() => store.value?.heroMobile?.url);

const assortments = computed(() => store.value?.assortmentsCollection.items.filter(Boolean) || []);
const services = computed(() => store.value?.servicesCollection.items?.filter(Boolean) || []);
const contacts = computed(() => store.value?.contactsCollection.items?.filter(Boolean) || []);
const storeName = computed(() => store.value?.name || '');

const head = () => ({
  title: onMyStorePage.value ? t('store.my_store') : `${store.value?.name} - ${store.value?.title}`,
  description: onMyStorePage.value ? t('store.my_store_info') : store.value?.title,
  link: [{ rel: 'canonical', href: `${config.baseUrl}${path}` }],
});
useHead(head());
</script>

<script lang="ts">
export default defineComponent({
  name: 'StorePage',
});
</script>
